import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

import { useMask } from "@react-input/mask";
import { useForm } from "react-hook-form";

type FormDataType = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  service: string;
  message: string;
};

export default function ContactUs() {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<FormDataType>();

  const { ref: hookFormRef, ...phoneRegister } = register("phone", {
    required: "Phone is required",
    minLength: { value: 19, message: "Enter full phone number" }
  });

  const inputRef = useMask({
    mask: "+44 (___) ___-__-__",
    replacement: { _: /\d/ }
  });

  const setRefs = (el: HTMLInputElement | null) => {
    inputRef.current = el;
    hookFormRef(el);
  };

  const onSubmit = async (data: FormDataType) => {
    setLoading(true);
    setError(null);

     const { first_name, last_name, ...rest } = data;

    let text = "";
    for (const [key, value] of Object.entries(data)) {
      text += `${key} - ${value}\n`;
    }

    const payload = {
      email: data.email,
      text,
      problemType: data.service,
      to_email: "support@londonappsco.com",
      subject: 'Contact Support Form',
    };

    try {
      const res = await fetch(
        "https://rocknlabs.com/api/support/request_form",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        }
      );

      if (!res.ok) {
        throw new Error("Server error");
      }

      setSubmitted(true);
      reset();
    } catch (err) {
      setError("Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen font-sans"
      style={{ backgroundColor: "#FEF7EB" }}
    >
    <style>{`
      input:-webkit-autofill,
      input:-webkit-autofill:hover,
      input:-webkit-autofill:focus {
        -webkit-box-shadow: 0 0 0px 1000px white inset;
      }
    `}</style>
      <Navbar />
      <section className="relative pt-40 pb-24 px-6 md:px-16 overflow-hidden">
        <div
          className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full opacity-20 pointer-events-none"
          style={{ backgroundColor: "#0073E5", filter: "blur(100px)" }}
        />
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <p
              className="text-xs font-semibold uppercase tracking-widest mb-4"
              style={{ color: "#0073E5" }}
            >
              Contact Us
            </p>
            <h1 className="text-5xl md:text-6xl font-bold text-black leading-tight mb-6">
              Let&apos;s talk about your project
            </h1>
            <p className="text-gray-500 leading-relaxed mb-10 max-w-md">
              Fill in the form and we&apos;ll get back to you within 24 hours.
              Prefer email? Reach us directly at support@londonappsco.com
            </p>
            <div className="space-y-5 text-sm text-gray-600">
              <div className="flex items-start gap-4">
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0"
                  style={{ backgroundColor: "#D4F419" }}
                >
                  <i className="ri-map-pin-line text-black"></i>
                </div>
                <div>
                  <p className="font-semibold text-black">
                    London App Syndicate LTD
                  </p>
                  <p className="text-gray-500 mt-1">
                    International House, 22-28 Wood Street
                    <br />
                    Doncaster, England, DN1 3LW
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0"
                  style={{ backgroundColor: "#D4F419" }}
                >
                  <i className="ri-phone-line text-black"></i>
                </div>
                <a
                  href="tel:+447520663996"
                  className="hover:underline cursor-pointer"
                >
                  +447520663996
                </a>
              </div>
              <div className="flex items-center gap-4">
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0"
                  style={{ backgroundColor: "#D4F419" }}
                >
                  <i className="ri-mail-line text-black"></i>
                </div>
                <a
                  href="mailto:support@londonappsco.com"
                  className="hover:underline cursor-pointer"
                >
                  support@londonappsco.com
                </a>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-8 md:p-10">
            {error ? (
              <div className="justify-items-center text-center py-12">
                <div className="flex items-center justify-center rounded-full mx-auto mb-5">
                  <img
                    className="w-12"
                    src="/src/pages/contact-us/img/error.svg"
                  />
                </div>
                <h3 className="text-xl font-bold text-black mb-2">Oops!</h3>
                <p className="text-gray-500 text-sm">
                  Something went wrong while sending your message. <br />
                  Please try again later.
                </p>
                <button
                  onClick={() => {
                    setError(null);
                    reset();
                  }}
                  className="w-[100%] max-w-[250px] mt-6 flex items-center justify-center gap-2 text-black font-semibold px-6 py-3.5 rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap text-sm"
                  style={{ backgroundColor: "#D4F419" }}
                  onMouseEnter={e =>
                    (e.currentTarget.style.backgroundColor = "#c2e010")
                  }
                  onMouseLeave={e =>
                    (e.currentTarget.style.backgroundColor = "#D4F419")
                  }
                >
                  Try Again
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            ) : submitted ? (
              <div className="justify-items-center text-center py-12">
                <div
                  className="w-14 h-14 flex items-center justify-center rounded-full mx-auto mb-5"
                  style={{ backgroundColor: "#D4F419" }}
                >
                  <i className="ri-check-line text-black text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-black mb-2">
                  Message sent!
                </h3>
                <p className="text-gray-500 text-sm">
                  We&apos;ll get back to you within 24 hours.
                </p>
                <button
                  onClick={() => {
                    setSubmitted(false);
                    reset();
                  }}
                  className="w-[100%] max-w-[250px] mt-6 flex items-center justify-center gap-2 text-black font-semibold px-6 py-3.5 rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap text-sm"
                  style={{ backgroundColor: "#D4F419" }}
                  onMouseEnter={e =>
                    (e.currentTarget.style.backgroundColor = "#c2e010")
                  }
                  onMouseLeave={e =>
                    (e.currentTarget.style.backgroundColor = "#D4F419")
                  }
                >
                  Send Another Message
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                      First Name
                    </label>
                    <input
                      {...register("first_name", {
                        required: "First name is required",
                        minLength: { value: 2, message: "Min 2 characters" }
                      })}
                      type="text"
                      placeholder="James"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-gray-400"
                    />
                    {errors.first_name?.message && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.first_name.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                      Last Name
                    </label>
                    <input
                      {...register("last_name", {
                        required: "Last name is required",
                        minLength: { value: 2, message: "Min 2 characters" }
                      })}
                      type="text"
                      placeholder="Smith"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-gray-400"
                    />
                    {errors.last_name?.message && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.last_name.message}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Email
                  </label>
                  <input
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Invalid email address"
                      }
                    })}
                    type="email"
                    placeholder="james@company.com"
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-gray-400"
                  />
                  {errors.email?.message && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.email.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Phone
                  </label>
                  <input
                    {...phoneRegister}
                    ref={setRefs}
                    type="tel"
                    placeholder="+44 000 000 0000"
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-gray-400"
                  />
                    {errors.phone?.message && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.phone.message}
                      </p>
                    )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Service
                  </label>
                  <select
                    {...register("service", {
                      required: "Please select a service"
                    })}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-gray-400 bg-white cursor-pointer"
                  >
                    <option value="">Select a service</option>
                    <option value="Custom Software Development">
                      Custom Software Development
                    </option>
                    <option value="Mobile App Development">
                      Mobile App Development
                    </option>
                    <option value="UI/UX Design">UI/UX Design</option>
                    <option value="IT Consulting">IT Consulting</option>
                    <option value="Digital Marketing">Digital Marketing</option>
                    <option value="QA & Testing">QA &amp; Testing</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.service?.message && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.service.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Message
                  </label>
                  <textarea
                    {...register("message", {
                      required: "Message is required",
                      minLength: { value: 20, message: "Min 20 characters" },
                      maxLength: { value: 500, message: "Max 500 characters" }
                    })}
                    rows={4}
                    placeholder="Tell us about your project..."
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-gray-400 resize-none"
                  ></textarea>
                  {errors.message?.message && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.message.message}
                    </p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 text-black font-semibold px-6 py-3.5 rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap text-sm"
                  style={{ backgroundColor: "#D4F419" }}
                  onMouseEnter={e =>
                    (e.currentTarget.style.backgroundColor = "#c2e010")
                  }
                  onMouseLeave={e =>
                    (e.currentTarget.style.backgroundColor = "#D4F419")
                  }
                >
                  {loading ? "Sending..." : "Send message"}
                  <i className="ri-arrow-right-line"></i>
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
